/**
 * Routing decision for protected admin routes.
 *
 * Extracted from ProtectedRoute because the ordering of these checks is what
 * caused the "perceived logout" bug of Sept 2026: the guard read a
 * not-yet-loaded roles array as "no permission" and sent signed-in club
 * admins to the Access Denied page, which they (reasonably) understood as
 * being logged out. The rules, in order:
 *
 *  - auth still settling → spinner
 *  - no user → login
 *  - IDSeat admin → render
 *  - the route demands a role/club and the profile hasn't SETTLED → spinner
 *    (fetchProfile retries and always settles, so this is bounded)
 *  - profile settled with an ERROR and the demand isn't met → relogin
 *    (session intact but unusable; a fresh sign-in repairs it — never
 *    "Access Denied")
 *  - profile settled cleanly but the demand isn't met → unauthorized
 *  - otherwise → render
 *
 * Workspaces (venues/tenants/kiosks): a membership-only user has no user_roles
 * row but does have a workspace, and counts as a club admin when they have
 * one. A route may demand a workspace KIND; then the active workspace must be
 * of that kind.
 */

export type WorkspaceKind = 'tenant' | 'venue' | 'operator';

/**
 * 'switch': the person works in a workspace of the kind the route needs, just
 * not the active one — switch to it rather than deny.
 * 'home': kiosk staff on an admin-only kiosk page — back to the orders.
 */
export type RouteDecision = 'spinner' | 'login' | 'relogin' | 'unauthorized' | 'render' | 'switch' | 'home';

export function resolveRouteDecision(args: {
  loading: boolean;
  authReady: boolean;
  hasUser: boolean;
  profileSettled: boolean;
  profileError: boolean;
  isIdseatAdmin: boolean;
  userRoles: string[];
  requiredRole?: string;
  requireClubAccess?: boolean;
  clubId: string | null;
  /** Workspace the route is for; when set, the active workspace must be of this kind. */
  requiredWorkspace?: WorkspaceKind;
  activeWorkspaceKind?: WorkspaceKind | null;
  /** Number of workspaces the user has; a membership-only user has no user_roles row. */
  workspaceCount?: number;
  /** The kinds among the user's workspaces (to switch rather than deny). */
  availableWorkspaceKinds?: WorkspaceKind[];
  /** The active workspace's role (owner / admin / staff). */
  activeWorkspaceRole?: string | null;
  /** 'admin': the page is for owners and admins of the workspace, not staff. */
  requiredWorkspaceRole?: 'admin';
}): RouteDecision {
  if (args.loading || (args.hasUser && !args.authReady)) return 'spinner';
  if (!args.hasUser) return 'login';
  if (args.isIdseatAdmin) return 'render';

  const hasAnyWorkspace = (args.workspaceCount ?? 0) > 0;
  const roleOk =
    !args.requiredRole ||
    args.userRoles.includes(args.requiredRole) ||
    (args.requiredRole === 'club_admin' && hasAnyWorkspace);
  const clubOk = args.requiredWorkspace
    ? args.activeWorkspaceKind === args.requiredWorkspace
    : !args.requireClubAccess || !!args.clubId;
  if (roleOk && clubOk) {
    if (args.requiredWorkspaceRole === 'admin' && args.activeWorkspaceRole === 'staff') return 'home';
    return 'render';
  }

  // The demand is not met — but is that real, or just not-loaded-yet?
  if (!args.profileSettled) return 'spinner';
  if (args.profileError) return 'relogin';
  const neededKind = args.requiredWorkspace ?? (args.requireClubAccess ? 'tenant' : undefined);
  if (roleOk && neededKind && (args.availableWorkspaceKinds ?? []).includes(neededKind)) return 'switch';
  return 'unauthorized';
}
